import React from "react";
import { Invoice } from "../../../../features/groups/groupSlice";
import InvoiceActions from "./InvoiceActions";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
import {
  formatCurrency,
  formatDate,
  getStatusBgColor,
  getStatusTextColor,
  getStatusIcon,
  InvoiceStatusType,
  InvoiceStatus,
} from "../utils";

interface InvoiceCardProps {
  invoice: Invoice;
  variant: "table-row" | "card";
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, variant }) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const {
    invoice_number,
    invoice_date,
    invoice_amount,
    invoice_currency,
    invoice_status,
  } = invoice;

  const statusLabels = {
    [InvoiceStatus.PAID]: language?.groupSubscription?.paid,
    [InvoiceStatus.PENDING]: language?.groupSubscription?.pending,
    [InvoiceStatus.OVERDUE]: language?.groupSubscription?.overdue,
    [InvoiceStatus.CANCELLED]: language?.groupSubscription?.cancelled,
  };

  if (variant === "table-row") {
    return (
      <tr className="hover:bg-gray-50 transition-colors">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {invoice_number}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(invoice_date)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatCurrency(invoice_amount, invoice_currency)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBgColor(
              invoice_status as InvoiceStatusType
            )} ${getStatusTextColor(invoice_status as InvoiceStatusType)}`}
          >
            {getStatusIcon(invoice_status as InvoiceStatusType)}
            <span className="ml-1 capitalize">
              {statusLabels[invoice_status as InvoiceStatusType] || "Unknown"}
            </span>
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <InvoiceActions invoice={invoice} />
        </td>
      </tr>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col xs:flex-row xs:items-center xs:space-x-3 space-y-2 xs:space-y-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {invoice_number}
              </h3>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit ${getStatusBgColor(
                  invoice_status as InvoiceStatusType
                )} ${getStatusTextColor(invoice_status as InvoiceStatusType)}`}
              >
                {getStatusIcon(invoice_status as InvoiceStatusType)}
                <span className="ml-1 capitalize">
                  {" "}
                  {statusLabels[invoice_status as InvoiceStatusType] || "Unknown"}
                </span>
              </span>
            </div>
            <div className="mt-2 flex flex-col xs:flex-row xs:items-center xs:space-x-4 space-y-1 xs:space-y-0 text-sm text-gray-500">
              <span>{formatDate(invoice_date)}</span>
              <span className="text-base sm:text-lg font-semibold text-gray-900">
                {formatCurrency(invoice_amount, invoice_currency)}
              </span>
            </div>
          </div>
          <div className="flex-shrink-0 self-end">
            <InvoiceActions invoice={invoice} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCard;
